import { useState } from 'react';
import { nomeDoProvedorOpencode, type ModeloOpencode } from '@remoto/shared';
import { CampoDeEscolha } from '../componentes/campos';
import { IconeFavorito } from '../componentes/icones';
import type { OpcaoDoSeletor } from '../componentes/Seletor';
import { provedorDoCatalogo, usarAdiado, usarCatalogo } from './dados';
import { descricaoDoModelo } from './regras';

/** Uma opção do seletor a partir de um modelo do catálogo. */
function opcaoDoModelo(m: ModeloOpencode): OpcaoDoSeletor {
  return {
    id: m.id,
    rotulo: m.nome,
    // O id vai junto: dois "GLM 5.3" de provedores diferentes só se separam
    // por ele, e é ele que o dono vai reconhecer no painel do provedor.
    descricao: `${m.id} · ${descricaoDoModelo(m)}`,
    icone: m.favorito ? <IconeFavorito tamanho={12} className="text-amber-300" /> : undefined,
  };
}

/**
 * Escolher um modelo do catálogo do provedor ATIVO (F2: o NanoGPT; o
 * OpenRouter, guardado), com busca (D12).
 *
 * É o `CampoDeEscolha` da casa com a busca do `Seletor` — não um seletor
 * novo. A busca vai ao servidor (`?busca=`, adiada enquanto a pessoa digita),
 * e a lista vazia mostra os favoritos primeiro.
 *
 * `aoEscolher` devolve o MODELO inteiro, e não só o id: quem chama precisa dos
 * `esforcos` dele para refazer o seletor de esforço — e um modelo achado pela
 * busca não está na lista que a tela carregou ao abrir.
 */
export function EscolhaDeModelo({
  rotulo,
  valor,
  aoEscolher,
  conhecidos,
  dica,
  vazio,
  excluir,
  desabilitado = false,
}: {
  rotulo: string;
  valor: string | null;
  aoEscolher: (modelo: ModeloOpencode) => void;
  /** Modelos já vistos, para o rótulo do valor atual quando a busca o filtrou. */
  conhecidos: ReadonlyMap<string, ModeloOpencode>;
  dica?: string;
  vazio?: string;
  /** Ids que não entram na lista (os que já são favoritos, por exemplo). */
  excluir?: readonly string[];
  desabilitado?: boolean;
}): React.JSX.Element {
  const [busca, setBusca] = useState('');
  const termo = usarAdiado(busca, 250);
  const { data, isFetching } = usarCatalogo(termo);

  const achados = (data?.modelos ?? []).filter((m) => !excluir?.includes(m.id));
  // A busca diz em QUAL catálogo procura: com dois provedores, "o catálogo" sozinho não diz.
  const provedor = provedorDoCatalogo(data);
  const opcoes = achados.map(opcaoDoModelo);

  /*
    O valor atual entra na lista quando a busca o tirou dela — no fim, para
    não passar à frente do que casou com a busca. Sem ele, o campo fechado
    mostraria "escolha…" enquanto a pessoa digita, como se a escolha tivesse
    sumido.
  */
  if (valor !== null && !opcoes.some((o) => o.id === valor)) {
    const conhecido = conhecidos.get(valor);
    opcoes.push(
      conhecido
        ? opcaoDoModelo(conhecido)
        : { id: valor, rotulo: valor, descricao: 'fora do catálogo carregado' },
    );
  }

  const porId = new Map<string, ModeloOpencode>(achados.map((m) => [m.id, m]));

  return (
    <CampoDeEscolha
      rotulo={rotulo}
      valor={valor}
      opcoes={opcoes}
      dica={dica}
      vazio={vazio}
      desabilitado={desabilitado}
      aoEscolher={(id) => {
        const modelo = porId.get(id) ?? conhecidos.get(id);
        if (modelo) aoEscolher(modelo);
      }}
      busca={{
        valor: busca,
        aoMudar: setBusca,
        dica: provedor ? `buscar no catálogo do ${nomeDoProvedorOpencode(provedor)}` : 'buscar no catálogo de modelos',
        carregando: isFetching || termo !== busca,
      }}
    />
  );
}
